import type { ShopperBasketsV2, ShopperProducts } from '@/scapi';

const GIFT_CARD_PRODUCT_TYPE = 'giftCard';

/** Variation attribute id for gift card denomination / cut selection. */
export const GIFT_CARD_SIZE_ATTRIBUTE_ID = 'giftCardSize';

/**
 * Whether a product is a sellable gift card (`c_productType === giftCard`).
 * Accepts a loose object so callers can pass SCAPI Product types that expose
 * custom attributes only via the project's augmented schemas.
 */
export function isGiftCardProduct(product?: object | null): boolean {
    if (!product) {
        return false;
    }
    return (product as { c_productType?: string }).c_productType === GIFT_CARD_PRODUCT_TYPE;
}

/**
 * Resolve the display price for a gift card cut. Prefers the matching variant price;
 * falls back to parsing the cut value as a number.
 */
export function getGiftCardAmountPrice(
    product: {
        variants?: Array<{
            price?: number;
            variationValues?: { [key: string]: string };
        }>;
    },
    cutValue: string
): number | undefined {
    const variantPrice = product.variants?.find(
        (variant) => variant.variationValues?.[GIFT_CARD_SIZE_ATTRIBUTE_ID] === cutValue
    )?.price;
    if (variantPrice != null) {
        return variantPrice;
    }

    const parsed = Number(cutValue);
    return Number.isFinite(parsed) ? parsed : undefined;
}

/**
 * Whether the basket holds at least one gift card product. Gift cards cannot be used to pay for
 * gift card products. Checks the basket hook flag first, then product line items against a
 * product map (`c_productType === giftCard`).
 */
export function basketHasGiftCardProduct(
    basket?: ShopperBasketsV2.schemas['Basket'] | null,
    productMap?: Record<string, ShopperProducts.schemas['Product']>
): boolean {
    if (basket?.c_hasGiftCardProduct) {
        return true;
    }

    if (!productMap) {
        return false;
    }

    return (basket?.productItems ?? []).some(
        (item) => item.productId && productMap[item.productId]?.c_productType === GIFT_CARD_PRODUCT_TYPE
    );
}
